import { formatYears } from '~/commonComponents/CommonFunctions';
import type { IUser } from '~/models';

interface INamedReceiver {
  full_name?: string;
  first_name?: string;
  last_name?: string;
}

/**
 * Display name for a shift's professional from the raw API receiver:
 * prefer `full_name`, else join `first_name` + `last_name`, else undefined.
 */
export const getProfessionalName = (receiver?: INamedReceiver | null): string | undefined =>
  receiver?.full_name ||
  `${receiver?.first_name ?? ''} ${receiver?.last_name ?? ''}`.trim() ||
  undefined;

/**
 * Years of experience as a number, which is what `CandidateCard` takes.
 *
 * `exactYearsOfExperience` is the precise figure; `years_of_experience` is a
 * legacy display string ("3+ years") kept for professionals who predate it, so
 * it is parsed rather than shown. Neither present means the card shows 0 — the
 * honest reading of "we don't know".
 */
export const toYearsOfExperience = (candidate: IUser | null | undefined): number => {
  const exact = candidate?.professional_info?.exactYearsOfExperience;

  if (typeof exact === 'number' && !Number.isNaN(exact)) {
    return exact;
  }

  const parsed = Number.parseInt(String(candidate?.years_of_experience ?? ''), 10);

  return Number.isNaN(parsed) ? 0 : parsed;
};

/**
 * Years of experience as copy, e.g. "5 Years", which is what `CandidateDetails`
 * takes: the exact count when the professional filed one, else the legacy
 * free-text `years_of_experience` as-is.
 *
 * `useCandidate().yearsOfExperience` is this; it is exported on its own for a
 * caller with no office or job to hand the hook (the perm Candidate Details page).
 */
export const toExperienceLabel = (candidate: IUser | null | undefined): string | undefined =>
  candidate?.professional_info?.exactYearsOfExperience != null
    ? formatYears(candidate.professional_info.exactYearsOfExperience)
    : candidate?.years_of_experience;
